import { ComponentObservable, componentObserver } from '../../component-observer';
import { currentBreakpoint } from '../../utils/resize.utils';
import { FirstLevelMenuItem, getMenuItems, MenuType, SecondLevelMenuItem } from '../../api/menu-api';
import { Header, header } from '../header/header.module';
import { ResourcesList } from './resources-center-list.module';
import { ResourcesOverview } from './resources-overview.module';
import { Param, readUrlParameter, writeToUrl } from '../../utils/url.utils';
import { Icon } from '../../general/icons/Icon';
import { createHtmlElementFromString } from '../../utils/html.utils';
import { CenterModule, CenterType } from '../../utils/center.module';
import { ResourcesCenterDetailModule } from './resources-center-detail.module';
import { ResourcesCenterCustomModule } from './resources-center-custom.module';
import { favoritesList as favoritesListModule } from '../favorites-list/favorites-list.module';

export enum ResourcesView {
  OVERVIEW,
  RESOURCES,
  DETAIL,
  CUSTOM
}

enum ResourcesTabId {
  TAB_ALL = 'tab-all',
  TAB_OVERVIEW = 'tab-overview',
  TAB_ALL_RESOURCES = '87bd3c09-484d-4ad5-8f4e-f96a15040377'
}

/**
 * Initializes and renders the resources center.
 */
class ResourcesCenter implements ComponentObservable, CenterModule {
  /**
   * Selector of resources center that must be initialized in javascript.
   */
  componentSelector = '.resources-center';

  /**
   * The translations for the resources-center
   */
  translations: { [key: string]: string } = null;

  /**
   * The menu items that will be shown on the overview page.
   */
  overviewFilters = [];

  /**
   * Store the header menu items.
   */
  menuItems: FirstLevelMenuItem[] = [];

  /**
   * Store the last menu selection and search phrase.
   * This is the selection before the user searched using the search field. After
   * the user removes the search, this selection will be restored.
   */
  lastMenuSelection: {
    category: FirstLevelMenuItem;
    tab: SecondLevelMenuItem;
    searchPhrase: string;
  } = {
    category: null,
    tab: null,
    searchPhrase: null
  };

  /**
   * The last number of search results for the menu items.
   */
  lastTaxonomyCounts: Record<string, number>;
  lastTagCounts: Record<string, number>;

  /**
   * Currently selected first level menu item.
   */
  currentSelectedCategory: string = null;

  /**
   * If the header menu has been initialized
   */
  headerMenuInitialized = false;

  /**
   * The header component of the resources center.
   */
  resourcesHeader: Header = null;

  /**
   * The view of the products with filter etc.
   */
  resourcesList: ResourcesList = null;

  /**
   * The overview page for desktop only.
   */
  resourcesOverview: ResourcesOverview = null;

  /**
   * The detail page when a resource is selected.
   */
  resourcesCenterDetail: ResourcesCenterDetailModule = null;

  /**
   * The custom page when a ventilator is selected
   */
  resourcesCenterCustom: ResourcesCenterCustomModule = null;

  /**
   * The current view that is shown in the resources center.
   */
  currentView: ResourcesView = ResourcesView.OVERVIEW;

  /**
   * Abort controller used to cancel all event listeners on the tab elements (first level navigation).
   */
  tabListenersAbortController = new AbortController();

  menuTaxonomies = [];

  /**
   * Markup for the favorites list
   */
  createFavoritesListMarkup(): string {
    return `
  <div>
    <aside id="favorites-list" class="favorites-list" aria-hidden="true">
      <div class="favorites-list__header">
        <div class="favorites-list__header-wrapper">
          <h3 class="favorites-list__title">${this.translations.favoritesListTitle}</h3>
          <span class="tag tag--grey"></span>
          <button class="button button--overlay button--icon-only favorites-list__close-button">
            <span class="button__icon-single">
              ${Icon.close}
            </span>
          </button>
        </div>
      </div>
      <div class="favorites-list__content">
        <div class="favorites-list__wrapper">
          <div class="favorites-list__empty-state">
            <p class="favorites-list__empty-state-title text-color-light">${this.translations.favoritesListEmptyState}</p>
          </div>
        </div>
      </div>
    </aside>
  </div>`;
  }

  /**
   * Create the resources center. Fetches the menu items and initializes the sub components
   */
  createResourcesCenter(root: HTMLElement): void {
    // add class to html element, for specific resources-center styling
    document.documentElement.classList.add('html--resources-center');

    // Create header template
    this.createHeader(root);

    // Add favorites list to the resources center
    this.setupFavoritesList(root);
    //
    // and header menu, if not already rendered
    if (!this.headerMenuInitialized) {
      // otherwise render the header menu
      this.fetchHeaderMenuItems().then(async (menuItems) => {
        this.menuTaxonomies = menuItems;
        this.headerMenuInitialized = true;
        this.menuItems = menuItems;
        this.renderHeaderMenu(menuItems);
        this.createSubComponents(root);
        this.setContentPosition();
      });
    }
  }

  /**
   * Creates the template for the header and setup translations.
   * @param root The resources center root element
   */
  createHeader(root: HTMLElement): void {
    // Create header from template and append it to the resources center
    const headerTemplate = createHtmlElementFromString(
      header.createTemplate(this.translations.headerTitle, this.translations.more, CenterType.RESOURCES_CENTER)
    );
    root.append(headerTemplate);

    // Set translations
    headerTemplate.dataset.labelAllDevices = this.translations.allVentilators;
    headerTemplate.dataset.labelAll = this.translations.all;
    headerTemplate.dataset.labelOverview = this.translations.overview;
    headerTemplate.dataset.labelMobileFilter = this.translations.filter;
    headerTemplate.dataset.labelMobileResetFilter = this.translations.mobileResetFilter;
    headerTemplate.dataset.labelMobileBackToAllCategoriesButton = this.translations.mobileBackToAllCategories;
    headerTemplate.dataset.labelOverview = this.translations.overview;

    // If there was a ventilator ID in the URL, preselect it in the ventilator dropdown
    const ventilatorIdFromUrl = readUrlParameter(Param.VENTILATOR);
    if (ventilatorIdFromUrl) {
      headerTemplate.dataset.selectedDevice = ventilatorIdFromUrl;
    }
  }

  /**
   * Render the header menu with first and second level items.
   */
  renderHeaderMenu(menuItems: FirstLevelMenuItem[]): void {
    const headerElement = document.querySelector<HTMLElement>('.section--header');
    headerElement.dataset.tabs = JSON.stringify(menuItems);

    const subMenus = {};
    for (const item of menuItems) {
      subMenus[item.id] = item.secondaryCategories;
    }

    headerElement.dataset.subMenu = JSON.stringify(subMenus);
    header.initialize(headerElement).then(() => {
      this.setupResourceSearch(headerElement);
    });
  }

  /**
   * Initialize listeners for the resource search input field and the cancel search button.
   */
  setupResourceSearch(headerElement: HTMLElement): void {
    const headerSearchField = headerElement.querySelector<HTMLInputElement>('.search-field__input');

    // if the URL contains a previous search phrase -> set it
    const searchPhraseFromUrl = readUrlParameter(Param.SEARCH_PHRASE);
    if (searchPhraseFromUrl) {
      headerSearchField.value = decodeURIComponent(searchPhraseFromUrl);

      // show the search field when a search phrase was given
      header.toggleSearch(headerElement, false);
      // show the all tab
      this.showAllCategoriesTab(headerElement, true);
    }

    // listen for changes in the input field (load results when the user submits the search)
    headerSearchField.addEventListener(
      'keyup',
      () => {
        setTimeout(
        () => {
          const currentInput = headerSearchField.value;
          const currentCategory = this.getFirstLevelMenuItemFromId(readUrlParameter(Param.CATEGORY));
          const currentTab = this.getSecondLevelMenuItemFromId(readUrlParameter(Param.TAB));

          if (currentInput.length > 0) {
            // remove custom page or product id from the URL
            writeToUrl({ param: Param.SEARCH_PHRASE, value: encodeURIComponent(currentInput) }, [
              Param.RESOURCE_DETAIL_ID,
              Param.RESOURCE_DETAIL_TYPE,
              Param.CUSTOM_URL,
              Param.QR_VIEW
            ]);

            this.resourcesList.updateFilterWithNewSearchPhrase(
              currentInput,
              this.currentView === ResourcesView.RESOURCES
            );
            this.updateMobileMenuHeadingAndBackButton(headerElement, null, null, 'results');
          } else {
            this.removeSearchText();
            return;
          }

          if (this.currentView !== ResourcesView.RESOURCES) {
            // Remove resource detail from url on tab click
            writeToUrl([], [Param.RESOURCE_DETAIL_TYPE, Param.RESOURCE_DETAIL_ID, Param.CUSTOM_URL], false, false);

            // if we are not on the products page, switch to it
            this.currentView = ResourcesView.RESOURCES;
            this.renderView();
          }

          // show the 'all' tab only if there has not been a search phrase before, otherwise just keep the current menu selection
          if (this.lastMenuSelection.searchPhrase === null) {
            const breakpoint = currentBreakpoint();
            const isMobile = breakpoint.isMobilePortrait || breakpoint.isMobileLandscape;
            if (!isMobile) {
              // on mobile don't select the 'all' tab automatically. Stay on the first level menu
              this.changeHeaderMenuForSearch(headerElement);
            } else {
              this.resourcesList.updateFirstLevelMenuSelection(null, true);
              this.showAllCategoriesTab(headerElement, true);
            }

            // save the current menu selection, so that we can return to it after completing the search
            this.lastMenuSelection.category = currentCategory;
            this.lastMenuSelection.tab = currentTab;
          }

          // save the current search phrase so next time the user searches we know if it was the first search or follow up search
          this.lastMenuSelection.searchPhrase = currentInput;
        }, 1200);
      },
      false
    );
    // reset the search when the user clicks the 'remove text' button in the search field
    const removeTextButton = headerElement.querySelector<HTMLElement>('#search-field-remove-text-button');
    removeTextButton.addEventListener(
      'click',
      () => {
        this.removeSearchText();
      },
      false
    );
  }

  /**
   * Reset the filters (sidebar). This function gets called by the header when the user selects
   * the 'all' tab of the tab bar.
   */
  resetFilters(): void {
    this.resourcesList?.resetFilters();
  }

  /**
   * Add a filter for a ventilator with the given ID. This function is called from the header,
   * when the user selects a ventilator from the dropdown.
   */
  updateFilterWithNewVentilator(itemId: string): void {
    this.resourcesList?.updateFilterWithNewVentilator(itemId);
  }

  /**
   * Remove the search phrase from the search input field. This clears the input field, removes the search filter,
   * and restores the menu and sub menu selection to the state before searching.
   */
  removeSearchText(): void {
    const parameters = [];
    const parametersToRemove = [Param.SEARCH_PHRASE, Param.QR_VIEW];

    if (this.lastMenuSelection.category?.id) {
      parameters.push({ param: Param.CATEGORY, value: this.lastMenuSelection.category.id });
    } else {
      parametersToRemove.push(Param.CATEGORY);
      parametersToRemove.push(Param.TAB);
    }

    if (this.lastMenuSelection.tab?.id) {
      parameters.push({ param: Param.TAB, value: this.lastMenuSelection.tab.id });
    } else {
      parametersToRemove.push(Param.TAB);
    }

    // also remove it from the URL
    writeToUrl(parameters, parametersToRemove, false, true);
    window.location.reload();
  }

  /**
   * Update the search result tags for the first and second level menu items.
   * If they are not visible yet, they will get visible.
   */
  updateSearchResultCountTags(taxonomyCounts: Record<string, number>, tagCounts: Record<string, number>): void {
    if (taxonomyCounts) {
      const allResourceCategoryIds = [//'87bd3c09-484d-4ad5-8f4e-f96a15040377',
        'f66a98d8-2978-4296-9180-042d0b629acc',
        '6b7894cc-2016-47b0-9e32-777c496aca47',
        '417c9daa-89ba-459a-ae88-ad0f27491866',
        '5699a6c3-a78c-4084-a176-0e171af50e02',
        '9c6adb57-1e6d-4093-b053-0bde92417df0',
        '723f7e89-e154-474b-9add-f680aed0243f',
        '469d4866-8e2b-4e63-9132-e0d67daf4ad4'];
      const tagsToId = [
          'document',
          'presentation',
          'article',
          'video',
          'elearning',
          'customerVoices',
          'scientificPublication',
      ]
      const resourceCenterElement = document.querySelector('.resources-center');
      const moreTabs = [...document.querySelectorAll('ul[data-reference-id="more-tabs"] > li > a')].map(item => item.id);
      const menuTabs = resourceCenterElement.querySelectorAll<HTMLElement>(
        'ul[role="tablist"] li, .sub-menu .sub-menu__item'
      );
      let totalCounts = 0;
      const tabs = JSON.parse(document.querySelector<HTMLElement>('.section--header').dataset.tabs);

      // update the menu items counter tags
      for (const menuTab of menuTabs) {
        const tabId = menuTab.firstElementChild.id;
        if (tabId !== ResourcesTabId.TAB_ALL && tabId !== ResourcesTabId.TAB_OVERVIEW && tabId !== 'sub-menu_all') {
          // only set a number if it is not the all or overview tab
          let resultCount = taxonomyCounts[tabId];

          if (menuTab.classList.contains('selector-item') && tabId !== ResourcesTabId.TAB_ALL_RESOURCES) {
            const secondaryCategories = this.menuTaxonomies.filter((item) => item.id === tabId)[0].secondaryCategories;
            resultCount = 0;

            secondaryCategories.forEach((secondaryCategory) => {
              resultCount += taxonomyCounts[secondaryCategory.id] || 0;
            })
          }

          if (allResourceCategoryIds.indexOf(tabId) > -1) {
            // It's a tag tab from All resources
            resultCount = tagCounts[tagsToId[allResourceCategoryIds.indexOf(tabId)]];
          }

          const hasResults = resultCount > 0;

          const counterTag = menuTab.querySelector<HTMLElement>('.tag');

          // show the tag
          menuTab.classList.remove('selector-item--hidden');
          counterTag.classList.remove('tag--hidden');

          if (hasResults) {
            // update the number of results for the tab
            counterTag.innerText = resultCount.toString();
          } else {
            // hide the whole menu item
            //menuTab.classList.add('selector-item--hidden');
            counterTag.innerText = '0';
          }

          if (menuTab.classList.contains('selector-item')) {
            moreTabs.push(tabId);
          }
        }
      }

      /*for (const mainTabId of moreTabs) {
        const mainCount = taxonomyCounts[mainTabId];
        if (mainCount > 0) {
          totalCounts += mainCount;
          console.log(totalCounts, mainCount, mainTabId);
        }
      }*/
      totalCounts = Object.values(tagCounts).reduce((acc, currentValue) => acc + currentValue, 0);

      for (const menuTab of menuTabs) {
        const tabId = menuTab.firstElementChild.id;
        if (tabId === ResourcesTabId.TAB_ALL_RESOURCES) {
          const counterTag = menuTab.querySelector<HTMLElement>('.tag');

          // show the tag
          menuTab.classList.remove('selector-item--hidden');
          counterTag.classList.remove('tag--hidden');

          if (totalCounts > 0) {
            counterTag.innerText = totalCounts.toString();
          } else {
            counterTag.innerText = '0';
          }
        }
      }

      // update the mobile header count tag
      let taxonomyForResults;
      const currentTab = readUrlParameter(Param.TAB);
      const currentCategory = readUrlParameter(Param.CATEGORY);
      if (currentTab && currentTab !== 'all') {
        taxonomyForResults = currentTab;
      } else if (currentCategory && currentCategory !== 'all') {
        taxonomyForResults = currentCategory;
      }
      if (taxonomyForResults) {
        const mobileHeaderCounterTag = resourceCenterElement.querySelector<HTMLElement>(
          '.navigation-mobile__selected-category .tag'
        );
        if (mobileHeaderCounterTag) {
          mobileHeaderCounterTag.innerText = `${taxonomyCounts[taxonomyForResults]}`;
        }
      }
    }
  }

  /**
   * Hides the result tags. This is necessary when the user removes the search.
   */
  hideSearchResultCountTagsAndShowAllMenuItems(): void {
    const headerElement = document.querySelector('.section--header');
    const categoryTabs = headerElement.querySelectorAll<HTMLElement>(
      'ul[role="tablist"] li, .sub-menu .sub-menu__item'
    );
    for (const categoryTab of categoryTabs) {
      const counterTag = categoryTab.querySelector<HTMLElement>('.tag');
      counterTag?.classList.add('tag--hidden');

      // show menu items again, that were previously hidden because they contained no results
      const tabId = categoryTab.firstElementChild.id;
      if (tabId !== ResourcesTabId.TAB_ALL && tabId !== ResourcesTabId.TAB_OVERVIEW) {
        categoryTab.classList.remove('selector-item--hidden');
      }
    }
  }

  /**
   * Programmatically select a category and tab in the header menu.
   * @param headerElement: The header root element
   * @param categoryId: The id of the category that should get selected or null for the overview page.
   * @param tabId: The id of the tab that should get selected or null for no selection.
   * @param preventReload: Do not reload the product page, only update the header selection.
   */
  changeHeaderMenuSelection(
    headerElement: HTMLElement,
    categoryId: string,
    tabId: string,
    preventReload = false
  ): void {
    const tabList = headerElement.querySelector('ul[role="tablist"]');
    const breakpoint = currentBreakpoint();
    const isMobile = breakpoint.isMobilePortrait || breakpoint.isMobileLandscape;
    const actualTabId = tabId !== 'all' ? tabId : null;
    const customUrl = readUrlParameter(Param.CUSTOM_URL);
    const detailUrl = readUrlParameter(Param.RESOURCE_DETAIL_ID);

    if (!categoryId && !customUrl && !isMobile) {
      // if no category and customUrl given on desktop -> switch to the overview page
      categoryId = ResourcesTabId.TAB_OVERVIEW;
    } else if (!categoryId && !customUrl && isMobile) {
      // show the first level menu again
      header.showMobileMenu(headerElement);
    }

    // deselect the currently active tab
    const selectedTab = tabList.querySelector('.selector-item--active');
    selectedTab?.classList.remove('selector-item--active');

    if (categoryId) {
      // set the category (first level item) that should get selected as active
      // querySelector with attribute selector is necessary because numbers as IDs are not valid CSS selectors
      tabList.querySelector(`[id='${categoryId}']`).parentElement.classList.add('selector-item--active');

      // create the sub menu for the respective first level option
      const headerSubNavigation = headerElement.querySelector<HTMLElement>('.header__sub-navigation');
      headerSubNavigation.removeAttribute('aria-hidden');
      this.createSubMenu(headerElement, categoryId, actualTabId);
    }

    if (categoryId === ResourcesTabId.TAB_ALL) {
      // if the 'all' category should get selected -> display it
      this.showAllCategoriesTab(headerElement, true);
    } else if (categoryId === ResourcesTabId.TAB_OVERVIEW) {
      // if the 'overview' category should get selected -> display it
      this.showAllCategoriesTab(headerElement, false);
    }

    // update the URL parameters to reflect the current category/tab selection
    if (categoryId === ResourcesTabId.TAB_ALL) {
      writeToUrl({ param: Param.CATEGORY, value: 'all' });
      this.resourcesList.updateFirstLevelMenuSelection(null, false);

      if (isMobile) {
        // On mobile hide the menu if the 'all' category was selected -> directly go to the results
        header.hideMobileMenu(headerElement);
      }
    } else if (categoryId === ResourcesTabId.TAB_OVERVIEW || !categoryId) {
      writeToUrl([], [Param.CATEGORY], true, true);
      this.resourcesList.updateFirstLevelMenuSelection(null, false);
    } else {
      writeToUrl({ param: Param.CATEGORY, value: categoryId });
      this.resourcesList.updateFirstLevelMenuSelection(this.getFirstLevelMenuItemFromId(categoryId), !preventReload);
    }

    // if not on the overview page, reload the product catalog
    const regularCategoryWasSelected = categoryId !== ResourcesTabId.TAB_OVERVIEW;
    const reload = !preventReload && regularCategoryWasSelected;
    if (tabId !== null) {
      writeToUrl({ param: Param.TAB, value: tabId }, null, false, false);
      if (tabId !== 'all') {
        this.resourcesList.updateSecondLevelMenuSelection(this.getSecondLevelMenuItemFromId(tabId), reload);
      }
    } else {
      writeToUrl([], [Param.CATEGORY], true, true);
      this.resourcesList.updateSecondLevelMenuSelection(null, reload);
    }

    if (!regularCategoryWasSelected && !isMobile && !detailUrl) {
      // If the overview category was selected on desktop -> go to overview page
      this.currentView = ResourcesView.OVERVIEW;
      this.renderView();
    }
  }

  /**
   * When coming from the overview page (clicked on a category card), this function changes the header menu selection
   * to the selected category.
   * @param headerElement: The header root element
   * @param categoryId: The id of the category that should get selected.
   */
  changeHeaderFromOverview(headerElement: HTMLElement, categoryId: string): void {
    const tabList = headerElement.querySelector('ul[role="tablist"]');

    // deselect the currently active tab
    const selectedTab = tabList.querySelector('.selector-item--active');
    selectedTab?.classList.remove('selector-item--active');

    if (categoryId) {
      // set the category (first level item) that should get selected as active
      // querySelector with attribute selector is necessary because numbers as IDs are not valid CSS selectors
      let selectedTab = tabList.querySelector(`[id='${categoryId}']`);
      if (!selectedTab) {
        selectedTab = headerElement
          .querySelector('ul[data-reference-id="more-tabs"')
          .querySelector(`[id='${categoryId}']`);
        headerElement
          .querySelector('ul[data-reference-id="more-tabs"')
          .dispatchEvent(new CustomEvent('onitemselect', { detail: { target: selectedTab.parentElement } }));
      }
      selectedTab.parentElement.classList.add('selector-item--active');

      // create the sub menu for the respective first level option
      const headerSubNavigation = headerElement.querySelector<HTMLElement>('.header__sub-navigation');
      headerSubNavigation.removeAttribute('aria-hidden');
      this.createSubMenu(headerElement, categoryId, null);
    }

    // update the URL parameters to reflect the current category/tab selection
    writeToUrl({ param: Param.CATEGORY, value: categoryId });
    this.resourcesList.updateFirstLevelMenuSelection(this.getFirstLevelMenuItemFromId(categoryId));
  }

  /**
   * When coming from a page that is not the resources list (e.g. overview, detail, etc), this function changes
   * the header menu selection to all/all.
   * @param headerElement: The header root element
   */
  changeHeaderMenuForSearch(headerElement: HTMLElement): void {
    const tabList = headerElement.querySelector('ul[role="tablist"]');
    const breakpoint = currentBreakpoint();
    const isMobile = breakpoint.isMobilePortrait || breakpoint.isMobileLandscape;

    // deselect the currently active tab
    const selectedTab = tabList.querySelector('.selector-item--active');
    selectedTab?.classList.remove('selector-item--active');

    // create the sub menu for the all tab
    const headerSubNavigation = headerElement.querySelector<HTMLElement>('.header__sub-navigation');
    headerSubNavigation.removeAttribute('aria-hidden');
    this.createSubMenu(headerElement, ResourcesTabId.TAB_ALL, null);

    // set the all tab as active
    // querySelector with attribute selector is necessary because numbers as IDs are not valid CSS selectors
    tabList.querySelector('[id="tab-all"]').parentElement.classList.add('selector-item--active');

    // if the 'all' category should get selected -> display it
    this.showAllCategoriesTab(headerElement, true);

    // update the URL parameters to reflect the current category/tab selection
    writeToUrl(
      [
        { param: Param.CATEGORY, value: 'all' },
        { param: Param.TAB, value: 'all' }
      ],
      null,
      true,
      true
    );
    this.resourcesList.updateFirstLevelMenuSelection(null, true);

    if (isMobile) {
      // On mobile hide the menu if the 'all' category was selected -> directly go to the results
      header.hideMobileMenu(headerElement);
    }
  }

  /**
   * @param headerElement: The header root element
   */
  changeHeaderMenuForQrView(headerElement: HTMLElement): void {
    const tabList = headerElement.querySelector('ul[role="tablist"]');

    // deselect the currently active tab
    const selectedTab = tabList.querySelector('.selector-item--active');
    selectedTab?.classList.remove('selector-item--active');

    // create the sub menu for the all tab
    const headerSubNavigation = headerElement.querySelector<HTMLElement>('.header__sub-navigation');
    headerSubNavigation.removeAttribute('aria-hidden');
    this.createSubMenuForQrCodeView(headerElement);

    // set the all tab as active
    // querySelector with attribute selector is necessary because numbers as IDs are not valid CSS selectors
    tabList.querySelector('[id="tab-all"]').parentElement.classList.add('selector-item--active');

    // hide all other tabs
    const otherTabs = tabList.querySelectorAll(':not([id="tab-all"])');
    for (const tab of otherTabs) {
      tab.parentElement.classList.add('selector-item--hidden');
    }

    // if the 'all' category should get selected -> display it
    this.showAllCategoriesTab(headerElement, true);
  }

  /**
   * Reset the text input that the user typed into the search field.
   */
  resetProductSearchInputField(): void {
    const headerElement = document.querySelector<HTMLElement>('.section--header');
    const headerSearchField = headerElement.querySelector<HTMLInputElement>('.search-field__input');
    headerSearchField.value = '';
  }

  /**
   * Renders the content depending on the current view.
   */
  renderView(): void {
    const resourcesRoot = document.querySelector<HTMLElement>(this.componentSelector);

    switch (this.currentView) {
      case ResourcesView.OVERVIEW:
        this.resourcesList.hide();
        this.resourcesCenterDetail.reset();
        this.resourcesCenterCustom.reset();
        this.resourcesOverview.render(
          resourcesRoot,
          this.menuItems.filter((item) => this.overviewFilters.indexOf(item.id) !== -1),
          true
        );
        break;
      case ResourcesView.RESOURCES:
        this.resourcesOverview.reset();
        this.resourcesCenterDetail.reset();
        this.resourcesCenterCustom.reset();
        this.resourcesList.render(resourcesRoot);
        this.resourcesList.show();
        break;
      case ResourcesView.DETAIL:
        this.resourcesOverview.reset();
        this.resourcesList.hide();
        this.resourcesCenterDetail.reset();
        this.resourcesCenterCustom.reset();
        this.resourcesCenterDetail.render(resourcesRoot);
        break;
      case ResourcesView.CUSTOM:
        this.resourcesOverview.reset();
        this.resourcesList.hide();
        this.resourcesCenterDetail.reset();
        this.resourcesCenterCustom.reset();
        this.resourcesCenterCustom.render(resourcesRoot);
    }
  }

  /**
   * Shows/hides the 'all' categories tab in the first level menu. If the 'all' tab is shown, the 'overview' tab is immediately hidden.
   * @param header The header element
   * @param show If the all tab should be visible
   */
  showAllCategoriesTab(header: HTMLElement, show: boolean): void {
    const allTab = header.querySelector<HTMLElement>(`#${ResourcesTabId.TAB_ALL}`)?.parentElement;
    const overviewTab = header.querySelector<HTMLElement>(`#${ResourcesTabId.TAB_OVERVIEW}`)?.parentElement;
    if (allTab) {
      allTab.classList.toggle('selector-item--hidden', !show);
      overviewTab.classList.toggle('selector-item--hidden-desktop', show);
    }
  }

  /**
   * Creates the tab navigation
   * @param headerElement The header element
   * @param selectedCategoryId
   */
  createTabNavigation(headerElement: HTMLElement, selectedCategoryId: string): void {
    const tabList = headerElement.querySelector('ul[role="tablist"]');

    const labelOverview = headerElement.dataset.labelOverview;
    if (headerElement.dataset.tabs) {
      const tabs = JSON.parse(headerElement.dataset.tabs);
      if (!tabs || !labelOverview) {
        // eslint-disable-next-line no-console
        console.error('Tabs and/or label for overview is missing');
      } else {
        // create the 'overview' item
        let items = `<li role="presentation" class="selector-item selector-item--hidden-mobile${
          selectedCategoryId === ResourcesTabId.TAB_OVERVIEW ? ' selector-item--active' : ''
        }"><a role="tab" id="${ResourcesTabId.TAB_OVERVIEW}" tabindex="0">${labelOverview}</a></li>`;

        items += `<li role="presentation" class="selector-item selector-item--hidden${
          selectedCategoryId === ResourcesTabId.TAB_ALL ? ' selector-item--active' : ''
        }" aria-hidden="true"><a role="tab" id="${ResourcesTabId.TAB_ALL}">${this.translations.all}</a></li>`;

        tabs.forEach((tab) => {
          items += `<li role="presentation" class="selector-item${
            selectedCategoryId === tab.id ? ' selector-item--active' : ''
          }"><a role="tab" id="${tab.id}" tabindex="0">${
            tab.label || tab.name
          }<span class="tag tag--counter tag--hidden"></span></a></li>`;
        });
        tabList.innerHTML = items;

        if (selectedCategoryId === ResourcesTabId.TAB_ALL) {
          // hide the sub menu if necessary (second level)
          this.showAllCategoriesTab(headerElement, true);
        }
      }
      // Add click listener to tabs to render the sub menu
      let tabElements = tabList.querySelectorAll<HTMLElement>('[role="tab"]');
      const toggleActiveState = (tab: HTMLElement): void => {
        const selectedTab = tabList.querySelector('.selector-item--active');
        selectedTab?.classList.remove('selector-item--active');
        tab.parentElement.classList.add('selector-item--active');
        const headerSubNavigation = headerElement.querySelector<HTMLElement>('.header__sub-navigation');

        // Remove resource detail and custom page from url on tab click
        const paramsToRemove = [
          Param.RESOURCE_DETAIL_TYPE,
          Param.RESOURCE_DETAIL_ID,
          Param.CUSTOM_URL,
          Param.CATEGORY,
          Param.TAB
        ];

        // Set view to render
        if (tab.id !== ResourcesTabId.TAB_OVERVIEW) {
          if (tab.id !== ResourcesTabId.TAB_ALL) {
            // if the all tab was selected, the sub menu will not be displayed
            headerSubNavigation.removeAttribute('aria-hidden');
            this.createSubMenu(headerElement, tab.id, null);
          }

          const menuItem = this.getFirstLevelMenuItemFromId(tab.id);
          // only reload the resources if we are on the resources list page already. Otherwise the reload is triggered by the URL change below
          this.resourcesList.updateFirstLevelMenuSelection(menuItem, this.currentView === ResourcesView.RESOURCES);
          const categoryIdForUrl = tab.id === ResourcesTabId.TAB_ALL ? 'all' : tab.id;

          writeToUrl(
            [
              { param: Param.CATEGORY, value: categoryIdForUrl },
              { param: Param.TAB, value: 'all' }
            ],
            [Param.RESOURCE_DETAIL_TYPE, Param.RESOURCE_DETAIL_ID, Param.CUSTOM_URL]
          );
        } else {
          // go to overview (no GET param necessary)
          writeToUrl(null, paramsToRemove);
        }
        this.resourcesHeader.updateMobileTabsOnSelect(headerElement);

        // store the current tab selection
        this.currentSelectedCategory = tab.id;
      };

      // Event listener to select a tab when a dropdown item is selected
      tabList.parentElement.addEventListener('ontabselected', (event: CustomEvent) =>
        toggleActiveState(event.detail.firstElementChild)
      );

      // Make sure that all visible tabs have click and key listeners
      tabList.parentElement.addEventListener('ontabschanged', () => {
        tabElements = tabList.querySelectorAll<HTMLElement>('[role="tab"]');

        // Remove first to avoid multiple listeners
        this.tabListenersAbortController.abort();
        this.tabListenersAbortController = new AbortController();

        tabElements.forEach((tab) => {
          // Set listeners
          tab.addEventListener('click', () => toggleActiveState(tab), {
            signal: this.tabListenersAbortController.signal
          });
          tab.addEventListener('keypress', (event) => (event.key === 'Enter' ? toggleActiveState(tab) : false), {
            signal: this.tabListenersAbortController.signal
          });
        });
      });
    }
  }

  /**
   * Creates the sub menu depending on the current tab
   * @param header
   * @param currentCategoryId The id of the category that is active
   * @param currentTabId The id of the tab that is active or null/'all' if 'all' tab should be active
   */
  createSubMenu(header: HTMLElement, currentCategoryId: string, currentTabId: string): void {
    const subMenuContainer = header.querySelector<HTMLElement>('.sub-menu');
    const breakpoint = currentBreakpoint();

    const headerSearchField = header.querySelector<HTMLInputElement>('.search-field__input');
    const currentSearchPhrase = headerSearchField.value;

    if (currentCategoryId === ResourcesTabId.TAB_OVERVIEW && (breakpoint.isTablet || breakpoint.isDesktop)) {
      subMenuContainer.parentElement.setAttribute('aria-hidden', 'true');
    } else if (header.dataset.subMenu && currentCategoryId !== ResourcesTabId.TAB_OVERVIEW) {
      const subMenu = JSON.parse(header.dataset.subMenu);

      // collect the sub menu items that need to be displayed
      let subMenuItems = [];

      if (currentCategoryId === ResourcesTabId.TAB_ALL) {
        subMenuContainer.classList.add('sub-menu--hidden-desktop');

        // if switching to the all tab, display all possible tabs from all categories
        for (const subMenuItemsList in subMenu) {
          subMenuItems = subMenuItems.concat(subMenu[subMenuItemsList]);
        }
      } else {
        subMenuContainer.classList.remove('sub-menu--hidden-desktop');

        // if a specific category was selected, display the tabs for that category
        subMenuItems = subMenu[currentCategoryId];
      }

      let items = `<li class="sub-menu__item${
        currentTabId === 'all' || currentTabId === null ? ' sub-menu__item--active' : ''
      }"><a id="sub-menu_all" tabindex="0">${this.translations.all}</a></li>`;
      if (subMenuItems) {
        subMenuItems.forEach((item) => {
          items += `<li class="sub-menu__item${currentTabId === item.id ? ' sub-menu__item--active' : ''}"><a id="${
            item.id
          }" tabindex="0">${item.label || item.name}<span class="tag tag--counter tag--hidden"></span></a></li>`;
        });
        subMenuContainer.innerHTML = items;
      } else {
        subMenuContainer.parentElement.setAttribute('aria-hidden', 'true');
      }

      if (currentSearchPhrase) {
        // If the user is currently searching, update the number of results for the sub menu items.
        this.updateSearchResultCountTags(this.lastTaxonomyCounts, this.lastTagCounts);
      }

      const toggleActiveState = (item: HTMLElement): void => {
        const selectedItem = subMenuContainer.querySelector('.sub-menu__item--active');
        selectedItem?.classList.remove('sub-menu__item--active');
        item.parentElement.classList.add('sub-menu__item--active');
        this.resourcesHeader.updateMobileSecondLevelNavigation(header, subMenuContainer);
        // Remove resource detail from url on tab click
        writeToUrl([], [Param.RESOURCE_DETAIL_TYPE, Param.RESOURCE_DETAIL_ID], true, true);

        if (item.id !== 'sub-menu_all') {
          writeToUrl({ param: Param.TAB, value: item.id });
          // update the filter for this second level menu item
          const menuItem = this.getSecondLevelMenuItemFromId(item.id);
          this.resourcesList.updateSecondLevelMenuSelection(menuItem);
        } else {
          writeToUrl({ param: Param.TAB, value: 'all' });
          this.resourcesList.updateSecondLevelMenuSelection(null);
        }

        // update the mobile headings
        const firstLevelItem = this.getFirstLevelMenuItemFromId(currentCategoryId);
        const secondLevelItem = this.getSecondLevelMenuItemFromId(item.id);
        this.updateMobileMenuHeadingAndBackButton(header, firstLevelItem, secondLevelItem, 'results');
      };

      // Add click listener to sub menu entries
      const menuItems = subMenuContainer.querySelectorAll<HTMLElement>('.sub-menu__item a');
      menuItems.forEach((item) => {
        item.addEventListener('click', () => toggleActiveState(item));
        item.addEventListener('keypress', (event) => (event.key === 'Enter' ? toggleActiveState(item) : false));
      });

      if (currentCategoryId && currentTabId && (breakpoint.isMobileLandscape || breakpoint.isMobilePortrait)) {
        const firstLevelItem = this.getFirstLevelMenuItemFromId(currentCategoryId);
        const secondLevelItem = this.getSecondLevelMenuItemFromId(currentTabId);
        const isDetailPage = !!readUrlParameter(Param.RESOURCE_DETAIL_ID);

        // When a category+tab are selected we need to display the search results immediately without menu/sub menu selection
        this.resourcesHeader.hideMobileMenu(header);
        // update the mobile headings
        setTimeout(() => {
          this.updateMobileMenuHeadingAndBackButton(
            header,
            firstLevelItem,
            secondLevelItem,
            isDetailPage ? 'detail' : 'results'
          );
        }, 200);
      } else {
        // When only a category is selected from the URL and the user opens the page on mobile
        const firstLevelItem = this.getFirstLevelMenuItemFromId(currentCategoryId);

        setTimeout(() => {
          this.updateMobileMenuHeadingAndBackButton(header, firstLevelItem, null, 'sub-menu');
        }, 200);
      }
    }
  }

  /**
   * Creates the sub menu depending on the current tab
   * @param header
   */
  createSubMenuForQrCodeView(header: HTMLElement): void {
    const subMenuContainer = header.querySelector<HTMLElement>('.sub-menu');
    const backButton = `<li><button class="sub-menu__back-button">${header.dataset.labelOverview}</button></li>`;
    subMenuContainer.innerHTML = backButton;

    const toggleActiveState = (): void => {
      const paramsToRemove = [Param.QR_VIEW, Param.CATEGORY, Param.TAB];
      // go to overview (no GET param necessary)
      writeToUrl(null, paramsToRemove);
      window.location.reload();
    };

    const backButtonElement = subMenuContainer.querySelector<HTMLButtonElement>('button');
    backButtonElement.addEventListener('click', () => toggleActiveState());
    backButtonElement.addEventListener('keypress', (event) => (event.key === 'Enter' ? toggleActiveState() : false));
  }

  /**
   * Update the menu heading and back button on mobile. Also shows/hides the filter on mobile.
   * @param header: Header root element.
   * @param category: The currently selected category.
   * @param tab: The currently selected tab or null if the 'all' tab has been selected.
   * @param level: If the mobile menu is currently showing the first or second level of the menu or the product results.
   */
  updateMobileMenuHeadingAndBackButton(
    header: HTMLElement,
    category: FirstLevelMenuItem,
    tab: SecondLevelMenuItem,
    level: 'menu' | 'sub-menu' | 'results' | 'detail'
  ): void {
    const currentTabLabel = header.querySelector<HTMLElement>('.navigation-mobile__selected-category');
    const mobileBackButton = header.querySelector<HTMLElement>('.navigation-mobile__back');
    const categoryContainer = header.querySelector<HTMLElement>('.navigation-mobile__category-container');

    if (currentTabLabel) {
      let menuHeading, backButtonLabel;
      const showCounter = level === 'results' && readUrlParameter(Param.SEARCH_PHRASE) !== null && category;

      if (level === 'sub-menu') {
        // when on the sub menu, display the name of the selected category (first level) as a heading
        // and as a label for the back button display something like 'back to categories'.
        menuHeading = category?.name;
        backButtonLabel = this.translations.mobileBackToAllCategories;
      } else if (level === 'results') {
        // when on the results page, either display the name of the selected tab (second level) as a heading
        // or if the 'all' option for a tab was selected, display the name of the category
        // The back button should be labeled with the category if there is a specific tab selected, otherwise
        // (if an 'all' tab is selected) it should be labeled with something like 'back to categories'.
        menuHeading = tab?.name || category?.name || this.translations.all;
        backButtonLabel = tab
          ? category.name
          : category
          ? this.translations.mobileBackToSubCategories
          : this.translations.mobileBackToAllCategories;
      } else if (level === 'detail') {
        menuHeading = '';
        backButtonLabel = this.translations.backToProducts;
      }

      // insert the heading before the counter tag
      const tabLabelContainsText = currentTabLabel.firstChild.nodeName.toLowerCase() !== 'span';
      if (tabLabelContainsText) {
        currentTabLabel.firstChild.nodeValue = menuHeading;
      } else {
        currentTabLabel.insertBefore(document.createTextNode(menuHeading), currentTabLabel.firstElementChild);
      }

      // hide or show the counter tag
      const counterTag = currentTabLabel.querySelector('.tag');
      counterTag.classList.toggle('tag--hidden', !showCounter);

      // add the label to the back button
      mobileBackButton.innerText = backButtonLabel;

      // and show the filter
      categoryContainer.classList.toggle('navigation-mobile__category-container--filter-visible', level === 'results');
    }
  }

  /**
   * Retrieve the header menu items.
   */
  fetchHeaderMenuItems(): Promise<FirstLevelMenuItem[]> {
    return getMenuItems(MenuType.MENU_RESOURCES);
  }

  /**
   * Get the first level menu item object from its ID. The menu items
   * are stored in this objects this.menuItems property.
   */
  getFirstLevelMenuItemFromId(menuItemId: string): FirstLevelMenuItem {
    let menuItem = null;
    for (const item of this.menuItems) {
      if (item.id === menuItemId) {
        menuItem = item;
      }
    }
    return menuItem;
  }

  /**
   * Get the second level menu item object from its ID. The menu items
   * are stored in this objects this.menuItems property.
   */
  getSecondLevelMenuItemFromId(menuItemId: string): SecondLevelMenuItem {
    let menuItem = null;
    for (const item of this.menuItems) {
      for (const secondLevelItem of item.secondaryCategories) {
        if (secondLevelItem.id === menuItemId) {
          menuItem = secondLevelItem;
        }
      }
    }
    return menuItem;
  }

    /**
   * Set the content position based on the presence of a notification.
   * Necessary for the Resources center due to its different structure compared to standard pages.
   */
    setContentPosition(): void {
      const importantMessage = document.querySelector<HTMLElement>('.important-message');

      if (importantMessage) {
        const globalSearchWrapper = document.querySelector<HTMLElement>('.global-search-wrapper');
        const sectionHeader = document.querySelector<HTMLElement>('.section--header');
        const mainNavigationWrapper = document.querySelector<HTMLElement>('.main-navigation-wrapper');
        const mainNavigationWrapperHeight = mainNavigationWrapper.offsetHeight;

        // Reposition the global search
        globalSearchWrapper.style.top = mainNavigationWrapperHeight + 'px';

        // Reposition the header on mobile
        if(window.innerWidth <= 767) {
          sectionHeader.style.top = mainNavigationWrapperHeight + 'px';
        }
      }
    }

  /**
   * Initialize all resources centers on the current page.
   * @param observe Boolean if the ResourcesCenterModule should listen for changes in the DOM and initialize dynamically added resources centers.
   */
  initializeAll(observe: boolean): void {
    const allResourceCenters = document.querySelectorAll(this.componentSelector);
    for (let i = 0; i < allResourceCenters.length; i++) {
      this.initialize(allResourceCenters[i] as HTMLElement);
    }

    if (observe) {
      this.startListening();
    }
  }

  /**
   * Listen for changes in DOM and initialize e catalogs when new ones appear
   */
  startListening(): void {
    componentObserver.subscribeListener(this);
  }

  /**
   * Creates the objects for the components that are used in the resources center.
   * @param root The resources center element
   */
  createSubComponents(root: HTMLElement): void {
    const categoryIdFromUrl = readUrlParameter(Param.CATEGORY);
    const tabIdFromUrl = readUrlParameter(Param.TAB);
    const searchPhraseFromUrl = readUrlParameter(Param.SEARCH_PHRASE);
    const customUrl = readUrlParameter(Param.CUSTOM_URL);
    const isQrView = readUrlParameter(Param.QR_VIEW);

    // Create header if not exists
    const headerElement = root.querySelector<HTMLElement>('.section--header');
    if (this.resourcesHeader === null) {
      this.resourcesHeader = header.prepareAndReturnHeader(true, this);

      let selectedCategoryId = categoryIdFromUrl || (customUrl ? null : ResourcesTabId.TAB_OVERVIEW);

      if (selectedCategoryId === 'all') {
        // the url only has 'all' for the all tab -> but we need the id of the all tab which is 'tab-all'
        selectedCategoryId = ResourcesTabId.TAB_ALL;
      }
      this.createTabNavigation(headerElement, selectedCategoryId);

      // when there was a category in the URL, use that one, otherwise show the overview page
      const selectedTabIdFromUrl = readUrlParameter(Param.TAB) || null;
      this.createSubMenu(headerElement, selectedCategoryId, selectedTabIdFromUrl);
    }

    // Create resources center overview (desktop only)
    if (this.resourcesOverview === null) {
      this.resourcesOverview = new ResourcesOverview(this.translations);
    }

    // Create view of the products with filters
    if (this.resourcesList === null) {
      const pathsParameter = root.dataset.paths || '';
      const languageList = JSON.parse(root.dataset.resourceLanguages);
      this.resourcesList = new ResourcesList(
        this.translations,
        pathsParameter,
        languageList,
        root.dataset.detailUrlArticles,
        root.dataset.detailUrlResources
      );

      if (!isQrView) {
        // Get the menu item object, so we can look up which filters we need
        const firstLevelItemId = categoryIdFromUrl || null;
        const firstLevelMenuItem = this.getFirstLevelMenuItemFromId(firstLevelItemId);
        this.resourcesList.updateFirstLevelMenuSelection(firstLevelMenuItem, false);

        if (firstLevelMenuItem) {
          // If there was a tab in the URL, select that one, otherwise select the 'all' tab.
          const secondLevelItemId = tabIdFromUrl || 'sub-menu_all';
          const secondLevelMenuItem = this.getSecondLevelMenuItemFromId(secondLevelItemId);
          this.resourcesList.updateSecondLevelMenuSelection(secondLevelMenuItem, false);
        }
      } else {
        this.changeHeaderMenuForQrView(headerElement);

        const firstLevelItemIds = categoryIdFromUrl?.split(',') || null;
        const firstLevelItems = [];
        if (firstLevelItemIds) {
          for (const firstLevelItemId of firstLevelItemIds) {
            firstLevelItems.push(this.getFirstLevelMenuItemFromId(firstLevelItemId));
          }
        }
        this.resourcesList.updateFirstLevelMenuSelection(firstLevelItems, false);

        const secondLevelItemIds = tabIdFromUrl?.split(',') || null;
        const secondLevelItems = [];
        if (secondLevelItemIds) {
          for (const secondLevelItemId of secondLevelItemIds) {
            secondLevelItems.push(this.getSecondLevelMenuItemFromId(secondLevelItemId));
          }
        }
        this.resourcesList.updateSecondLevelMenuSelection(secondLevelItems, false);
      }

      if (searchPhraseFromUrl) {
        this.resourcesList.updateFilterWithNewSearchPhrase(decodeURIComponent(searchPhraseFromUrl), false);
      }
    }

    // Create detail view
    if (this.resourcesCenterDetail === null) {
      this.resourcesCenterDetail = new ResourcesCenterDetailModule(
        this.translations,
        root.dataset.detailUrlArticles,
        root.dataset.detailUrlResources
      );

      // listen for url changes that are triggered by navigation in the resources center
      window.addEventListener('urlchange', () => {
        this.renderPageDependingOnUrlData(true);
      });

      // listen for url changes that are triggered by browser back/forward buttons
      window.addEventListener('popstate', () => {
        window.location.reload();
      });
    }

    // Create custom view
    if (this.resourcesCenterCustom === null) {
      this.resourcesCenterCustom = new ResourcesCenterCustomModule(this.translations);
    }

    // Check if url contains a product id
    this.renderPageDependingOnUrlData();

    // Render current view
    this.renderView();
  }

  /**
   * Checks if the url contains any product or page info and render the respective page.
   */
  renderPageDependingOnUrlData(reload?: boolean): void {
    const breakpoint = currentBreakpoint();
    const isMobile = breakpoint.isMobileLandscape || breakpoint.isMobilePortrait;

    const resourceId = readUrlParameter(Param.RESOURCE_DETAIL_ID);
    const resourceType = readUrlParameter(Param.RESOURCE_DETAIL_TYPE);
    const searchPhrase = readUrlParameter(Param.SEARCH_PHRASE);
    const category = readUrlParameter(Param.CATEGORY);
    const custom = readUrlParameter(Param.CUSTOM_URL);
    if (resourceId && resourceType) {
      writeToUrl([], [Param.CUSTOM_URL], true, true);
      this.currentView = ResourcesView.DETAIL;
    } else if (category || searchPhrase) {
      writeToUrl([], [Param.CUSTOM_URL], true, true);
      this.currentView = ResourcesView.RESOURCES;
    } else if (custom) {
      this.currentView = ResourcesView.CUSTOM;
    } else {
      writeToUrl([], [Param.CUSTOM_URL], true, true);
      this.currentView = isMobile ? ResourcesView.RESOURCES : ResourcesView.OVERVIEW;
    }
    if (reload) {
      this.renderView();
    }
  }

  /**
   * Initialize the given resources center.
   * @param root The resources center root element that should be initialized
   */
  initialize(root: HTMLElement): void {
    componentObserver.markElementAsInitialized(this.componentSelector, root);

    if (!this.translations) {
      this.translations = JSON.parse(root.dataset.translations);
    }

    if (this.overviewFilters.length === 0) {
      this.overviewFilters = root.dataset.overviewFilters.split(',');
    }

    // render
    this.createResourcesCenter(root);
  }

  /**
   * Creates the favorites list with the translations
   * @param root The resources center element
   */
  setupFavoritesList(root: HTMLElement): void {
    const favoritesListWrapper = createHtmlElementFromString(this.createFavoritesListMarkup());
    const favoritesList = favoritesListWrapper.firstElementChild as HTMLElement;
    favoritesList.dataset.favoritesListTitle = this.translations.favoritesListTitle;
    favoritesList.dataset.packagingUnit = this.translations.packagingUnit;
    favoritesList.dataset.material = this.translations.material;
    favoritesList.dataset.gtin = this.translations.gtin;
    favoritesList.dataset.pn = this.translations.pn;
    favoritesList.dataset.length = this.translations.length;
    favoritesList.dataset.forVentilator = this.translations.forVentilator;
    favoritesList.dataset.viewButton = this.translations.viewButton;
    favoritesList.dataset.detailButton = this.translations.detailButton;
    favoritesList.dataset.downloadButton = this.translations.downloadButton;
    favoritesList.dataset.source = this.translations.source;
    favoritesList.dataset.date = this.translations.date;
    favoritesList.dataset.lastUpdated = this.translations.lastUpdated;
    favoritesList.dataset.public = this.translations.public;
    favoritesList.dataset.new = this.translations.new;
    favoritesList.dataset.updated = this.translations.updated;
    favoritesList.dataset.languages = this.translations.languages;
    favoritesList.dataset.fileType = this.translations.fileType;
    favoritesList.dataset.fileSize = this.translations.fileSize;
    favoritesList.dataset.author = this.translations.author;
    favoritesList.dataset.errorMessage = this.translations.errorMessage;
    favoritesList.dataset.favoritesGoTo = this.translations.favoritesGoTo;
    favoritesList.dataset.labelFavoritesListAdded = this.translations.labelFavoritesListAdded;
    favoritesList.dataset.labelFavoritesListRemoved = this.translations.labelFavoritesListRemoved;

    root.append(favoritesListWrapper);

    favoritesListModule.createFavoritesList(favoritesListWrapper);
  }

  /**
   * Called from the header when the print button was clicked.
   */
  printPage(): void {
    window.print();
  }
}

const resourcesCenterModule = new ResourcesCenter();
export { resourcesCenterModule as resourcesCenter };
